import { $t } from '@/plugins/fluent';
import { getEnvironmentLong, getEnvironmentShort } from '@/utils/environment';
import { useFetch } from '@vueuse/core';
import { DateTime } from 'luxon';
import { ref, watchEffect } from 'vue';

export type DeploymentInfo =
    | {
          status: 'Loading';
          loadingMessage: string;
          environmentLong: string;
          environmentShort: string;
      }
    | {
          status: 'Success';
          commitShortHash: string;
          commitTimestampLong: string;
          commitTimestampShort: string;
          commitUrl: string;
          environmentLong: string;
          environmentShort: string;
      }
    | {
          status: 'Error';
          errorMessage: string;
          environmentLong: string;
          environmentShort: string;
      };

type VersionJsonResponse = {
    job: {
        startedAt: string;
        url: string;
    };
    commit: {
        branch: string;
        sha: string;
        shortSha: string;
        timestamp: string;
        url: string;
    };
};

export function useVersionInfo(versionJsonUrl: string) {
    const versionTimestamp = ref<string>('');

    const deploymentInfo = ref<DeploymentInfo>({
        status: 'Loading',
        loadingMessage: $t('loading-deployment-info'),
        environmentLong: getEnvironmentLong(import.meta.env.ENV_NAME),
        environmentShort: getEnvironmentShort(import.meta.env.ENV_NAME),
    });

    const { isFetching, data, error, execute } = useFetch<string>(versionJsonUrl);

    watchEffect(() => {
        if (isFetching.value) {
            deploymentInfo.value = {
                status: 'Loading',
                loadingMessage: $t('loading-deployment-info'),
                environmentLong: deploymentInfo.value.environmentLong,
                environmentShort: deploymentInfo.value.environmentShort,
            };
        }

        if (data.value) {
            try {
                const versionJson = JSON.parse(data.value) as VersionJsonResponse;
                const datetime = DateTime.fromISO(versionJson.commit.timestamp);
                versionTimestamp.value = versionJson.commit.timestamp;

                deploymentInfo.value = {
                    status: 'Success',
                    commitShortHash: versionJson.commit.shortSha,
                    commitTimestampLong: datetime.toLocaleString({
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    }),
                    commitTimestampShort: datetime.toLocaleString({
                        day: '2-digit',
                        month: '2-digit',
                    }),
                    commitUrl: versionJson.commit.url,
                    environmentLong: deploymentInfo.value.environmentLong,
                    environmentShort: deploymentInfo.value.environmentShort,
                };
            } catch (e) {
                throw new Error(`Parsing the version.json failed with error ${e}`);
            }
        }

        if (error.value) {
            deploymentInfo.value = {
                status: 'Error',
                errorMessage: $t('loading-deployment-info-failed'),
                environmentLong: deploymentInfo.value.environmentLong,
                environmentShort: deploymentInfo.value.environmentShort,
            };
            throw new Error(`Error on fetching deployment info: ${error.value}`);
        }
    });

    return { deploymentInfo, versionTimestamp, execute };
}
